/**
 * Cards.js - full-screen HTML overlay for star cards.
 *
 * Simple version: a fixed-position overlay div with a content div inside.
 * Click outside the card or press ESC to close.
 *
 * Supports optional voice buttons: if the card HTML contains a
 * `.card-voice-btn` with a `data-audio` attribute, clicking it plays
 * the audio file. The audio stops when the card is closed.
 */
export class Cards {
  constructor() {
    this.overlay = document.getElementById('card-overlay');
    this.content = document.getElementById('card-content');
    this.isShowing = false;
    this._audio = null;

    if (!this.overlay || !this.content) {
      console.error('Cards: #card-overlay or #card-content missing from DOM');
      return;
    }

    // Click outside the card content (i.e., on the backdrop) closes it
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) this.hide();
    });

    // ESC key closes
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isShowing) this.hide();
    });
  }

  show(html) {
    if (!this.content) return;
    this.content.innerHTML = html;
    this.overlay.classList.add('visible');
    this.isShowing = true;
    this._bindVoiceButtons();
  }

  hide() {
    if (!this.overlay) return;
    this.overlay.classList.remove('visible');
    this.isShowing = false;
    this._stopAudio();
    // Clear content after the fade-out completes
    setTimeout(() => {
      if (!this.isShowing && this.content) this.content.innerHTML = '';
    }, 300);
  }

  isOpen() {
    return this.isShowing;
  }

  /**
   * After card HTML is injected, find any .card-voice-btn and wire up
   * click → play/pause.
   */
  _bindVoiceButtons() {
    if (!this.content) return;
    const btns = this.content.querySelectorAll('.card-voice-btn');
    btns.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const src = btn.getAttribute('data-audio');
        if (!src) return;

        // Toggle: if this source is already playing, pause it
        if (this._audio && this._audio.src.endsWith(src) && !this._audio.paused) {
          this._audio.pause();
          btn.classList.remove('playing');
          return;
        }

        // Stop any existing audio before starting a new one
        this._stopAudio();

        this._audio = new Audio(src);
        this._audio.volume = 0.85;

        // Update button state
        btn.classList.add('playing');
        const icon = btn.querySelector('.voice-icon');
        if (icon) icon.textContent = '⏸';

        this._audio.addEventListener('ended', () => {
          btn.classList.remove('playing');
          if (icon) icon.textContent = '▶';
          this._audio = null;
        });

        this._audio.addEventListener('error', () => {
          btn.classList.remove('playing');
          if (icon) icon.textContent = '▶';
          btn.classList.add('error');
          btn.title = 'Audio file not found';
          this._audio = null;
        });

        this._audio.play().catch(() => {
          btn.classList.remove('playing');
          if (icon) icon.textContent = '▶';
        });
      });
    });
  }

  /** Stop and clean up any playing audio. */
  _stopAudio() {
    if (this._audio) {
      this._audio.pause();
      this._audio.currentTime = 0;
      this._audio = null;
    }
    // Reset all voice button states in the current card
    if (this.content) {
      this.content.querySelectorAll('.card-voice-btn.playing').forEach((btn) => {
        btn.classList.remove('playing');
        const icon = btn.querySelector('.voice-icon');
        if (icon) icon.textContent = '▶';
      });
    }
  }
}
